import { getFirebaseSingleton } from "@lib/firebase";
import { type ReactElement, useEffect } from "react";
import { Route, Switch, useRouteMatch } from "react-router-dom";

import { store } from "../../app/store";
import { AccountReactivationRoutes } from "../AccountReactivation";
import { ACCOUNT_REACTIVATION_PATH } from "../AccountReactivation/paths";
import { AUTH_PATH, IonLoginSignUpRoutes } from "../Auth";
import { CognitoTestPage } from "../Debug/CognitoTest";
import { E2E_TEST_PATH } from "../E2E/paths";
import { E2ERoutes } from "../E2E/Routes";
import { NotFoundPage } from "../Errors/NotFound";
import { CbhFeatureFlag, useCbhFlag } from "../FeatureFlags";
import { useLegacyStateContext } from "../LegacyStateBridge/context";
import { APP_V2_APP_EVENTS, logEvent } from "../lib/analytics";
import { AppLoadingPage } from "../lib/Layouts/LoadingPage/Page";
import { SHIFT_FEEDBACK_RESOLVER_PATH } from "../ShiftFeedback";
import { ShiftFeedbackRoutes } from "../ShiftFeedback/Routes";

export function AppV2PublicRoutes(): ReactElement {
  const match = useRouteMatch();

  const {
    session: { authStateHasLoaded },
  } = useLegacyStateContext();

  const isDebuggingEnabled = useCbhFlag(CbhFeatureFlag.WORKER_APP_INITIALIZATION_DEBUGGING, {
    defaultValue: false,
  });

  const { appLoadingPageLog } = useCbhFlag(CbhFeatureFlag.TRACK_INDEXEDDB_SPLASH_HANG, {
    defaultValue: {
      indexedDbOpenExperiment: false,
      appLoadingPageLog: {
        enabled: false,
        timeoutInMs: 10_000,
      },
    },
  });

  useEffect(() => {
    if (isDebuggingEnabled) {
      const firebaseUser = getFirebaseSingleton().currentUser;
      const sessionState = store.getState().session;

      logEvent(APP_V2_APP_EVENTS.APP_LOADING_PAGE_PUBLIC_ROUTES_PROPS, {
        authStateHasLoaded,
        matchUrl: match.url,
        matchPath: match.path,
        hasFirebaseUser: Boolean(firebaseUser),
        firebaseUserEmail: firebaseUser?.email,
        firebaseUserUid: firebaseUser?.uid,
        // Redux session state
        reduxIsAuthorized: sessionState.isAuthorized,
        reduxAuthStateHasLoaded: sessionState.authStateHasLoaded,
        reduxIsLoggingOut: sessionState.isLoggingOut,
        reduxIsVerifying: sessionState.isVerifying,
        reduxIsSignup: sessionState.isSignup,
        reduxUserId: sessionState.userId,
        reduxUserPhone: sessionState.profile?.phone,
        reduxHasEnv: Boolean(sessionState.env),
        reduxShowAlert: sessionState.showAlert,
        reduxActivePopups: Object.keys(sessionState.popups ?? {}).filter((key) =>
          Boolean((sessionState.popups as any)?.[key]?.visible)
        ),
      });
    }
  }, [isDebuggingEnabled, authStateHasLoaded, match.url, match.path]);

  if (!authStateHasLoaded) {
    return (
      <AppLoadingPage
        parentEventName={APP_V2_APP_EVENTS.APP_LOADING_PAGE_PUBLIC_ROUTES_PROPS}
        isDebuggingEnabled={isDebuggingEnabled || appLoadingPageLog.enabled}
        debugTimeoutInMs={appLoadingPageLog.timeoutInMs}
      />
    );
  }

  return (
    <Switch>
      <Route exact path={`${match.url}/cognito-test`}>
        <CognitoTestPage />
      </Route>
      <Route path={`${match.url}/${AUTH_PATH}`}>
        <IonLoginSignUpRoutes />
      </Route>
      <Route path={`${match.url}/${ACCOUNT_REACTIVATION_PATH}`}>
        <AccountReactivationRoutes />
      </Route>
      <Route path={`${match.url}/${E2E_TEST_PATH}`}>
        <E2ERoutes />
      </Route>
      <Route path={`${match.url}/${SHIFT_FEEDBACK_RESOLVER_PATH}`}>
        <ShiftFeedbackRoutes />
      </Route>
      <Route>
        <NotFoundPage />
      </Route>
    </Switch>
  );
}
